import React, { useEffect, useMemo, useState } from "react";
import { MdClose, MdAttachMoney, MdAccessTime, MdAutorenew } from "react-icons/md";
import api from "../../../../services/axios";
import {
  BillingType,
  BillingTypeName,
  ClientEquipmentOption,
  ClientTimesheetDetail,
  CreateServiceOrderPayload,
} from "../../../../interfaces/Financial";
import { toast } from "react-toastify";

interface ClientOption {
  id: number;
  legalName: string;
  tradeName: string;
}

interface ServiceOrderModalProps {
  month: number;
  year: number;
  onClose: () => void;
  onSuccess: () => void;
}

const monthsLabel = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const billingTypeOptions: {
  value: BillingType;
  icon: React.ReactNode;
  accent: string;
}[] = [
  {
    value: 1,
    icon: <MdAttachMoney className="h-5 w-5" />,
    accent: "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400",
  },
  {
    value: 2,
    icon: <MdAccessTime className="h-5 w-5" />,
    accent: "bg-dv-cyan-50 text-dv-cyan-600 dark:bg-dv-cyan-900/40 dark:text-dv-cyan-300",
  },
  {
    value: 3,
    icon: <MdAutorenew className="h-5 w-5" />,
    accent: "bg-dv-blue-50 text-dv-blue-600 dark:bg-dv-blue-900/40 dark:text-dv-blue-300",
  },
];

const ServiceOrderModal: React.FC<ServiceOrderModalProps> = ({
  month,
  year,
  onClose,
  onSuccess,
}) => {
  const [billingType, setBillingType] = useState<BillingType | null>(null);
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<number | "">("");
  const [details, setDetails] = useState<ClientTimesheetDetail[]>([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectedDetailIds, setSelectedDetailIds] = useState<Set<number>>(new Set());
  const [equipmentOptions, setEquipmentOptions] = useState<ClientEquipmentOption[]>([]);
  const [equipmentsLoading, setEquipmentsLoading] = useState(false);
  const [selectedEquipmentIds, setSelectedEquipmentIds] = useState<Set<number>>(new Set());
  const [amount, setAmount] = useState<string>("");
  const [amountTouched, setAmountTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      setClientsLoading(true);
      try {
        const res = await api.get<ClientOption[]>("/Client/Clients");
        setClients((res.data ?? []).filter((c: any) => c.flActive));
      } catch {
        toast.error("Failed to load clients.", { position: "top-right", autoClose: 4000 });
      } finally {
        setClientsLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (selectedClientId === "") {
      setDetails([]);
      setSelectedDetailIds(new Set());
      setEquipmentOptions([]);
      setSelectedEquipmentIds(new Set());
      return;
    }
    const fetchDetails = async () => {
      setDetailsLoading(true);
      try {
        const res = await api.get<ClientTimesheetDetail[]>(
          `/Financial/ClientTimesheetDetails`,
          { params: { clientId: selectedClientId, month, year } }
        );
        setDetails(res.data);
        // Pré-seleciona TODAS as horas apontadas disponíveis (não vinculadas) — assim a OS já nasce com elas.
        setSelectedDetailIds(
          new Set(res.data.filter((d) => d.linkedBillingId === null).map((d) => d.timeSheetDetailId))
        );
      } catch {
        setDetails([]);
        toast.error("Failed to load timesheet entries.", {
          position: "top-right",
          autoClose: 4000,
        });
      } finally {
        setDetailsLoading(false);
      }
    };
    const fetchEquipments = async () => {
      setEquipmentsLoading(true);
      try {
        const res = await api.get<ClientEquipmentOption[]>(
          `/Financial/ClientEquipmentOptions`,
          { params: { clientId: selectedClientId, month, year } }
        );
        setEquipmentOptions(res.data);
        const preselected = new Set(
          res.data.filter((e) => !e.alreadyBilledThisMonth).map((e) => e.equipmentId)
        );
        setSelectedEquipmentIds(preselected);
      } catch {
        setEquipmentOptions([]);
        setSelectedEquipmentIds(new Set());
      } finally {
        setEquipmentsLoading(false);
      }
    };
    fetchDetails();
    fetchEquipments();
  }, [selectedClientId, month, year]);

  const totalHours = useMemo(
    () =>
      details
        .filter((d) => selectedDetailIds.has(d.timeSheetDetailId))
        .reduce((acc, d) => acc + d.hoursWorked, 0),
    [details, selectedDetailIds]
  );

  const hourlyPreview = useMemo(() => {
    const selected = details.filter((d) => selectedDetailIds.has(d.timeSheetDetailId));
    let total = 0;
    const missingSet = new Set<string>();
    for (const d of selected) {
      total += d.hoursWorked * (d.projectHourlyRate ?? 0);
      if (d.projectBillingType === 2 && d.projectHourlyRate == null) {
        missingSet.add(d.projectName);
      }
    }
    return { total, missingRateProjects: Array.from(missingSet) };
  }, [details, selectedDetailIds]);

  const totalEquipmentValue = useMemo(
    () =>
      equipmentOptions
        .filter((e) => selectedEquipmentIds.has(e.equipmentId))
        .reduce((acc, e) => acc + e.monthlyValue, 0),
    [equipmentOptions, selectedEquipmentIds]
  );

  useEffect(() => {
    // OS por hora: valor = horas × taxa + equipamentos (mesmo cálculo autoritativo do backend).
    if (billingType === 2 && !amountTouched && hourlyPreview.total + totalEquipmentValue > 0) {
      setAmount((hourlyPreview.total + totalEquipmentValue).toFixed(2));
    }
  }, [billingType, hourlyPreview.total, totalEquipmentValue, amountTouched]);

  useEffect(() => {
    setAmountTouched(false);
  }, [selectedClientId, billingType]);

  const toggleEquipment = (id: number) => {
    setSelectedEquipmentIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const availableDetailIds = useMemo(
    () => details.filter((d) => d.linkedBillingId === null).map((d) => d.timeSheetDetailId),
    [details]
  );
  const allHoursSelected =
    availableDetailIds.length > 0 && availableDetailIds.every((id) => selectedDetailIds.has(id));

  const toggleAll = () => {
    setSelectedDetailIds(new Set(allHoursSelected ? [] : availableDetailIds));
  };

  const fmtBRL = (v: number) =>
    v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (billingType === null) {
      toast.warn("Select a billing type.", { position: "top-right", autoClose: 4000 });
      return;
    }
    if (selectedClientId === "") {
      toast.warn("Select a client.", { position: "top-right", autoClose: 4000 });
      return;
    }
    const parsed = parseFloat(amount.replace(",", "."));
    if (isNaN(parsed) || parsed <= 0) {
      toast.warn("Amount must be greater than zero.", { position: "top-right", autoClose: 4000 });
      return;
    }

    const payload: CreateServiceOrderPayload = {
      clientId: Number(selectedClientId),
      billingType,
      monthRef: month,
      yearRef: year,
      amount: parsed,
      timeSheetDetailIds: Array.from(selectedDetailIds),
      equipmentIds: Array.from(selectedEquipmentIds),
    };

    setSubmitting(true);
    try {
      await api.post("/Financial/ServiceOrder", payload);
      toast.success("Service Order created.", { position: "top-right", autoClose: 4000 });
      onSuccess();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data;
      toast.error(typeof msg === "string" ? msg : "Failed to create Service Order.", {
        position: "top-right",
        autoClose: 4000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  type DetailGroup = {
    key: string;
    employeeId: number;
    employeeName: string;
    projectId: number | null;
    projectName: string;
    totalHours: number;
    unlinkedDetailIds: number[];
    linkedDetailIds: number[];
    projectHourlyRate: number | null;
    projectBillingType: BillingType | null;
  };

  const groupedDetails = useMemo<DetailGroup[]>(() => {
    const map = new Map<string, DetailGroup>();
    for (const d of details) {
      const key = `${d.employeeId}|${d.projectId ?? "none"}`;
      let g = map.get(key);
      if (!g) {
        g = {
          key,
          employeeId: d.employeeId,
          employeeName: d.employeeName,
          projectId: d.projectId,
          projectName: d.projectName,
          totalHours: 0,
          unlinkedDetailIds: [],
          linkedDetailIds: [],
          projectHourlyRate: d.projectHourlyRate,
          projectBillingType: d.projectBillingType,
        };
        map.set(key, g);
      }
      g.totalHours += d.hoursWorked;
      if (d.linkedBillingId === null) g.unlinkedDetailIds.push(d.timeSheetDetailId);
      else g.linkedDetailIds.push(d.timeSheetDetailId);
    }
    return Array.from(map.values()).sort((a, b) => {
      if (a.employeeName !== b.employeeName) return a.employeeName.localeCompare(b.employeeName);
      return a.projectName.localeCompare(b.projectName);
    });
  }, [details]);

  const toggleGroup = (group: DetailGroup) => {
    if (group.unlinkedDetailIds.length === 0) return;
    setSelectedDetailIds((prev) => {
      const next = new Set(prev);
      const allSelected = group.unlinkedDetailIds.every((id) => next.has(id));
      if (allSelected) {
        group.unlinkedDetailIds.forEach((id) => next.delete(id));
      } else {
        group.unlinkedDetailIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/60 p-4 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="relative flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-dv-lg dark:bg-navy-800">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-bold text-navy-700 dark:text-white">
              Gerar Faturamento
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {monthsLabel[month - 1]} {year}
            </p>
          </div>
          <button
            aria-label="Fechar"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-navy-700 dark:hover:text-white"
          >
            <MdClose className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
            <div>
              <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-300">
                Tipo de Faturamento
              </label>
              <div className="grid grid-cols-3 gap-3">
                {billingTypeOptions.map((opt) => {
                  const active = billingType === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setBillingType(opt.value)}
                      className={`flex items-center gap-3 rounded-xl border p-3 text-left text-sm font-semibold transition-all ${
                        active
                          ? "border-dv-blue-500 bg-dv-blue-50 text-dv-blue-700 dark:border-dv-blue-400 dark:bg-dv-blue-900/20 dark:text-dv-blue-300"
                          : "border-gray-200 text-gray-700 hover:border-gray-300 dark:border-white/10 dark:text-gray-300 dark:hover:border-white/20"
                      }`}
                    >
                      <span
                        className={`flex h-9 w-9 items-center justify-center rounded-full ${opt.accent}`}
                      >
                        {opt.icon}
                      </span>
                      {BillingTypeName[opt.value]}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label htmlFor="so-client" className="mb-1.5 block text-sm font-semibold text-gray-700 dark:text-gray-300">
                Cliente
              </label>
              <select
                id="so-client"
                required
                disabled={clientsLoading}
                value={selectedClientId}
                onChange={(e) =>
                  setSelectedClientId(e.target.value === "" ? "" : Number(e.target.value))
                }
                className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white"
              >
                <option value="">{clientsLoading ? "Carregando clientes…" : "Selecione um cliente…"}</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.tradeName} ({c.legalName})
                  </option>
                ))}
              </select>
            </div>

            {selectedClientId !== "" && (
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Apontamentos do mês
                  </label>
                  {availableDetailIds.length > 0 && (
                    <label className="flex cursor-pointer items-center gap-1.5 text-xs font-semibold text-dv-blue-600 dark:text-dv-cyan-300">
                      <input
                        type="checkbox"
                        checked={allHoursSelected}
                        onChange={toggleAll}
                        className="h-3.5 w-3.5 rounded border-gray-300 text-dv-blue-600 accent-dv-blue-600 focus:ring-dv-blue-500"
                      />
                      Incluir todas as horas apontadas
                    </label>
                  )}
                </div>
                <div className="rounded-xl border border-gray-200 dark:border-white/10">
                  {detailsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="h-6 w-6 animate-spin rounded-full border-2 border-dv-blue-500 border-t-transparent" />
                    </div>
                  ) : groupedDetails.length === 0 ? (
                    <p className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                      Nenhum apontamento para este cliente em {monthsLabel[month - 1]} {year}.
                    </p>
                  ) : (
                    <div className="max-h-72 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-gray-50 dark:bg-navy-900">
                          <tr>
                            <th className="w-10 px-3 py-2"></th>
                            <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300">
                              Colaborador
                            </th>
                            <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300">
                              Projeto
                            </th>
                            <th className="px-3 py-2 text-right font-semibold text-gray-700 dark:text-gray-300">
                              Total horas
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupedDetails.map((g) => {
                            const allLinked = g.unlinkedDetailIds.length === 0;
                            const selectedCount = g.unlinkedDetailIds.filter((id) =>
                              selectedDetailIds.has(id)
                            ).length;
                            const checked =
                              !allLinked && selectedCount === g.unlinkedDetailIds.length;
                            const indeterminate =
                              selectedCount > 0 && selectedCount < g.unlinkedDetailIds.length;
                            return (
                              <tr
                                key={g.key}
                                className={`border-t border-gray-100 dark:border-gray-700 ${
                                  allLinked ? "opacity-50" : ""
                                }`}
                              >
                                <td className="px-3 py-2">
                                  <input
                                    type="checkbox"
                                    disabled={allLinked}
                                    checked={checked}
                                    ref={(el) => {
                                      if (el) el.indeterminate = indeterminate;
                                    }}
                                    onChange={() => toggleGroup(g)}
                                    className="h-4 w-4 rounded border-gray-300 text-dv-blue-600 accent-dv-blue-600 focus:ring-dv-blue-500"
                                  />
                                </td>
                                <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                                  {g.employeeName}
                                  {g.linkedDetailIds.length > 0 && (
                                    <span className="block text-[10px] italic text-gray-500 dark:text-gray-400">
                                      {g.linkedDetailIds.length} apontamento(s) já vinculado(s)
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                                  {g.projectName}
                                </td>
                                <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                                  {g.totalHours.toFixed(1)}h
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  {selectedDetailIds.size} item(ns) selecionado(s) · {totalHours.toFixed(1)} horas no total
                </p>
              </div>
            )}

            {selectedClientId !== "" && (
              <div>
                <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Equipamentos do cliente
                </label>
                <div className="rounded-xl border border-gray-200 dark:border-white/10">
                  {equipmentsLoading ? (
                    <div className="flex items-center justify-center py-6">
                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-dv-blue-500 border-t-transparent" />
                    </div>
                  ) : equipmentOptions.length === 0 ? (
                    <p className="px-4 py-4 text-center text-xs italic text-gray-500 dark:text-gray-400">
                      Nenhum equipamento alugado configurado para este cliente.
                    </p>
                  ) : (
                    <div className="max-h-56 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-gray-50 dark:bg-navy-900">
                          <tr>
                            <th className="w-10 px-3 py-2"></th>
                            <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300">
                              Equipamento
                            </th>
                            <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300">
                              Nº Série
                            </th>
                            <th className="px-3 py-2 text-right font-semibold text-gray-700 dark:text-gray-300">
                              Valor mensal
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {equipmentOptions.map((e) => {
                            const checked = selectedEquipmentIds.has(e.equipmentId);
                            const blocked = e.alreadyBilledThisMonth;
                            return (
                              <tr
                                key={e.equipmentId}
                                className={`border-t border-gray-100 dark:border-gray-700 ${
                                  blocked ? "opacity-50" : ""
                                }`}
                                title={
                                  blocked
                                    ? `Já cobrado em OS #${e.linkedBillingId} este mês`
                                    : ""
                                }
                              >
                                <td className="px-3 py-2">
                                  <input
                                    type="checkbox"
                                    disabled={blocked}
                                    checked={checked}
                                    onChange={() => toggleEquipment(e.equipmentId)}
                                    className="h-4 w-4 rounded border-gray-300 text-dv-blue-600 accent-dv-blue-600 focus:ring-dv-blue-500"
                                  />
                                </td>
                                <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                                  {e.model}
                                </td>
                                <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{e.serieNumber}</td>
                                <td className="px-3 py-2 text-right font-medium text-gray-700 dark:text-gray-300">
                                  {fmtBRL(e.monthlyValue)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  {selectedEquipmentIds.size} equipamento(s) · {fmtBRL(totalEquipmentValue)}
                </p>
              </div>
            )}

            <div>
              <label htmlFor="so-amount" className="mb-1.5 block text-sm font-semibold text-gray-700 dark:text-gray-300">
                Valor
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                  R$
                </span>
                <input
                  id="so-amount"
                  type="number"
                  required
                  min="0.01"
                  step="0.01"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => { setAmountTouched(true); setAmount(e.target.value); }}
                  className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-9 pr-3 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                />
              </div>
              {billingType === 2 ? (
                <div className="mt-1 space-y-0.5">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Total: <span className="font-semibold">{fmtBRL(hourlyPreview.total + totalEquipmentValue)}</span> ·{" "}
                    {totalHours.toFixed(1)}h ({fmtBRL(hourlyPreview.total)}) + equipamentos {fmtBRL(totalEquipmentValue)}
                  </p>
                  {hourlyPreview.missingRateProjects.length > 0 && (
                    <p className="text-xs font-medium text-amber-600 dark:text-amber-400">
                      sem valor/hora cadastrado: {hourlyPreview.missingRateProjects.join(", ")}
                    </p>
                  )}
                </div>
              ) : totalEquipmentValue > 0 ? (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Total da OS: valor informado + equipamentos {fmtBRL(totalEquipmentValue)} ={" "}
                  <span className="font-semibold">{fmtBRL((parseFloat(amount) || 0) + totalEquipmentValue)}</span>
                </p>
              ) : null}
            </div>
          </div>

          <div className="flex justify-end gap-3 border-t border-gray-100 px-6 py-4 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-white/15 dark:text-gray-200 dark:hover:bg-navy-700"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting || billingType === null || selectedClientId === ""}
              className="flex items-center justify-center gap-2 rounded-xl bg-ink-900 px-5 py-2.5 text-sm font-bold text-white shadow-dv-md transition-all hover:bg-ink-800 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-ink-900 dark:hover:bg-gray-100"
            >
              {submitting ? "Gerando…" : "Gerar faturamento"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ServiceOrderModal;
